import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, Shield, FileText } from 'lucide-react';
import axios from 'axios';
import { useBranding } from '../lib/BrandingContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { branding, loading: brandingLoading } = useBranding();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        username,
        password
      });

      const data = response.data;
      
      // Store token and user info
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));

      const nextPath = sessionStorage.getItem('postLoginRedirectPath');
      if (nextPath) {
        sessionStorage.removeItem('postLoginRedirectPath');
      }
      
      // Navigate to dashboard
      navigate(nextPath || '/dashboard');
    } catch (err) {
      // Handle axios errors
      if (err.response) {
        const status = err.response.status;
        const detail = err.response.data?.detail;
        
        if (status === 403) {
          setError(detail || '🚫 Access denied. Your account may be suspended. Please contact support.');
        } else if (status === 401) {
          setError(detail || '❌ Incorrect username or password');
        } else {
          setError(detail || 'Login failed');
        }
      } else {
        setError(err.message || 'An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: branding.login_background 
          ? `url(${branding.login_background}) center/cover` 
          : `linear-gradient(135deg, var(--primary-color, #1e40af) 0%, #0f172a 100%)`
      }}
    >
      {/* Background Overlay */}
      {branding.login_background && (
        <div className="absolute inset-0 bg-black/50" />
      )}

      <div className="relative w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          {branding.logo_url ? (
            <div className="inline-flex items-center justify-center mb-4">
              <img 
                src={branding.logo_url} 
                alt={branding.company_name || 'Logo'} 
                className="h-20 w-auto"
              />
            </div>
          ) : (
            <div 
              className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4"
              style={{ backgroundColor: `var(--primary-color, #1e40af)` }}
            >
              <Scale className="w-10 h-10 text-white" />
            </div>
          )}
          <h1 className="text-4xl font-bold text-white mb-2">
            {branding.company_name || 'TechOrange-Legal'}
          </h1>
          <p className="text-blue-200">Professional Legal Document Automation</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-800">Welcome Back</h2>
            <p className="text-gray-600 mt-2">Sign in to continue to your workspace</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                style={{ 
                  '--tw-ring-color': `var(--primary-color, #1e40af)`
                }}
                placeholder="Enter your username"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                style={{ 
                  '--tw-ring-color': `var(--primary-color, #1e40af)`
                }}
                placeholder="Enter your password"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading || brandingLoading}
              className="w-full text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
              style={{
                backgroundColor: `var(--primary-color, #1e40af)`
              }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5 mr-2" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Features */}
          <div className="mt-8 grid grid-cols-3 gap-4 pt-6 border-t border-gray-200">
            <div className="text-center">
              <FileText className="w-6 h-6 mx-auto mb-2" style={{ color: `var(--primary-color, #1e40af)` }} />
              <p className="text-xs text-gray-600">6 Bank Formats</p>
            </div>
            <div className="text-center">
              <Shield className="w-6 h-6 mx-auto mb-2" style={{ color: `var(--primary-color, #1e40af)` }} />
              <p className="text-xs text-gray-600">Secure</p>
            </div>
            <div className="text-center">
              <Scale className="w-6 h-6 mx-auto mb-2" style={{ color: `var(--primary-color, #1e40af)` }} />
              <p className="text-xs text-gray-600">AI-Powered</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-blue-200 text-sm mt-8">
          {branding.footer_text || '© 2025 TechOrange-Legal. All rights reserved.'}
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
